import * as React from 'react';

import { useAssessmentContext } from '../../context';
import IAssessmentElement from '../../models/IAssessmentElement';
import { IValidatorConfig, IValidatorResult } from '../../models/IValidator';
import useQuestionManager from '../../plugins/useQuestionManager';
import TextField from '../inputs/TextField';

interface IProps 
{
	question: IAssessmentElement,
	onValidatorResultChanged?(validatorResult: IValidatorResult): void,
	onResetValidation?(parentElement: IAssessmentElement | null): void,
	validatorConfig?: IValidatorConfig
}

export function TextInput(props: IProps)
{
	const questionManager = useQuestionManager();
	const assessmentContext = useAssessmentContext();
	const [answerValues, setAnswerValues] = React.useState<string[]>(assessmentContext.pageAnswers.filter(x => x.id == props.question.id)[0]?.value ?? []);
	const [hasFocus, setHasFocus] = React.useState<boolean>(false);

	// Sync with external updates from Redux (e.g., address lookup population)
	React.useEffect(() =>
	{
		const externalAnswers = assessmentContext.pageAnswers.filter(
			x => x.id == props.question.id
		)[0]?.value ?? [];
		
		// Only update if user is not actively editing
		if (!hasFocus)
			setAnswerValues(externalAnswers);
	},
	[
		assessmentContext.pageAnswers,
		props.question.id,
		hasFocus
	]);

	const onChange = (e: React.ChangeEvent<HTMLInputElement>) =>
	{
		const value = e.target.value
		setAnswerValues([value])

		//If value has been updated without focus e.g. auto filled then update stored value
		//If not then wait until onBlur for better performance
		if(!hasFocus)
			updateStoredAnswer([value])
	};

	const onBlur = () =>
	{
		updateStoredAnswer(answerValues)
		setHasFocus(false)
	};

	const updateStoredAnswer = (values: string[]) =>
	{
		questionManager.updateAnswerValue(props.question.id, values);
		if(props.onResetValidation && props.question.hasConditionalChildren)
			props.onResetValidation(props.question);
	};

	return <TextField
		onValidatorResultChanged={props.onValidatorResultChanged}
		validator={{
			validatorRules: questionManager.getQuestionValidatorRules(props.question),
			config: props.validatorConfig
		}}
		value={(answerValues.length) ? answerValues[0] : ''}
		answerValue={answerValues}
		onBlur={onBlur}
		onFocus={() => setHasFocus(true)}
		onChange={onChange}/>
}

export default TextInput
